// Typed client for /proteus-vision-ecom-ext/* — React port of the
// Angular ErpService. The router is mounted at the site root (no /api
// prefix), so each call uses `baseURL: '/'` to override the default
// axios client baseURL.

import api from '@/api/client'

const BASE = '/proteus-vision-ecom-ext'
const ROOT_AXIOS = { baseURL: '/' as const, withCredentials: true }

export interface ErpCustomer {
  id: string
  name: string
  phone: string
  email: string | null
  address: string | null
  priorOrderCount: number
  lastVisitDate: string | null
  lastOrderId: string | null
}

export interface ErpFieldOption {
  value: string
  label: string
  swatch?: string
  imageUrl?: string
}

export interface ErpFieldDef {
  key: string
  label: string
  type: 'text' | 'textarea' | 'number' | 'select' | 'tel' | 'email' | 'boolean'
  required?: boolean
  validate?: 'phone' | 'email' | 'required'
  placeholder?: string
  options?: ErpFieldOption[]
  control?: 'swatch' | 'pill' | 'select' | 'radio' | 'toggle' | 'text'
  unit?: string
  min?: number
  max?: number
}

export interface ErpCustomerSchema {
  fields: ErpFieldDef[]
}

export interface ErpCategory {
  id: string
  label: string
  children?: ErpCategory[]
}

export interface ErpProduct {
  id: string
  name: string
  category: string
  basePrice: number
  currency: string
  imageUrl: string
  metadata: Record<string, string | number | boolean>
}

export interface ErpProductDetail extends ErpProduct {
  description: string
  gallery: string[]
  availableStyleIds: string[]
}

export interface ErpStyle {
  id: string
  productId: string
  name: string
  imageUrl?: string
  priceDelta: number
}

export interface ErpPreferenceSchema {
  styleId: string
  fields: ErpFieldDef[]
}

export interface ErpMeasurementGroup {
  key: string
  label: string
  fields: ErpFieldDef[]
}

export interface ErpMeasurementSchema {
  styleId: string
  unit: string
  groups: ErpMeasurementGroup[]
}

export interface ErpPreviewLayer {
  when: Record<string, string | string[]>
  imageUrl?: string | null
  label?: string
}

export interface ErpPreviewSpec {
  styleId: string
  baseImageUrl: string
  aspect?: '1:1' | '4:3' | '3:4' | '16:9'
  tintAttribute?: string | null
  tintBlendMode?: 'multiply' | 'screen' | 'overlay' | 'soft-light'
  tintOpacity?: number
  annotations?: string[]
  captionTemplate?: string
  layers?: ErpPreviewLayer[]
  compositeImageEndpoint?: string
}

export interface ErpFeatureBadge {
  key: string
  fieldLabel: string
  label: string
  imageUrl?: string | null
  swatch?: string | null
}

export interface ErpCompositeResult {
  styleId: string
  imageUrl: string
  aspect?: '1:1' | '4:3' | '3:4' | '16:9'
  tintColor?: string | null
  tintOpacity?: number
  tintBlendMode?: string
  fallbackImageUrl?: string
  featureBadges?: ErpFeatureBadge[]
  description?: string
}

export interface ErpAiImageReference {
  label: string
  imageUrl: string
  mime?: string
}

export interface ErpAiImageSkippedRef extends ErpAiImageReference {
  reason: string
}

export interface ErpAiImagePreview {
  styleId: string
  prompt: string
  description: string
  referenceImages: ErpAiImageReference[]
  skippedReferences?: ErpAiImageSkippedRef[]
}

export interface ErpAiImageResult {
  styleId: string
  imageUrl: string
  description?: string
  source?: string
  prompt?: string
  referenceImages?: ErpAiImageReference[]
  skippedReferences?: ErpAiImageSkippedRef[]
  generatedAt?: string
}

export interface ErpOrderLine {
  productId: string
  styleId: string
  preferences: Record<string, unknown>
  measurements: Record<string, number>
  qty: number
  unitPrice: number
}

export interface ErpOrderCreateInput {
  customerId: string
  lines: ErpOrderLine[]
  notes?: string
}

export interface ErpOrderResult {
  id: string
  orderNumber: string
  status: string
  totalAmount: number
  currency: string
  createdAt: string
}

export interface ErpProductsPage {
  items: ErpProduct[]
  page: number
  pageSize: number
  total: number
}

export interface ErpListProductsParams {
  q?: string
  category?: string
  page?: number
  pageSize?: number
  sort?: 'price_asc' | 'price_desc' | 'popular' | 'newest'
  filters?: Record<string, string>
}

// ── Customers ──

export async function searchCustomers(q: string, limit = 20): Promise<ErpCustomer[]> {
  const params: Record<string, string> = { limit: String(limit) }
  if (q) params.q = q
  const res = await api.get<{ items: ErpCustomer[] }>(`${BASE}/customers`, { ...ROOT_AXIOS, params })
  return res.data.items ?? []
}

export async function getCustomerSchema(): Promise<ErpCustomerSchema> {
  const res = await api.get<ErpCustomerSchema>(`${BASE}/customers/schema`, ROOT_AXIOS)
  return res.data
}

export async function createCustomer(input: Record<string, string>): Promise<ErpCustomer> {
  const res = await api.post<ErpCustomer>(`${BASE}/customers`, input, ROOT_AXIOS)
  return res.data
}

// ── Catalogue ──

export async function listCategories(): Promise<ErpCategory[]> {
  const res = await api.get<{ categories: ErpCategory[] }>(`${BASE}/categories`, ROOT_AXIOS)
  return res.data.categories ?? []
}

export async function listProducts(p: ErpListProductsParams = {}): Promise<ErpProductsPage> {
  const params: Record<string, string> = {}
  if (p.q) params.q = p.q
  if (p.category) params.category = p.category
  if (p.page) params.page = String(p.page)
  if (p.pageSize) params.pageSize = String(p.pageSize)
  if (p.sort) params.sort = p.sort
  if (p.filters) {
    for (const [k, v] of Object.entries(p.filters)) {
      if (v !== undefined && v !== null && v !== '') params[`filter[${k}]`] = String(v)
    }
  }
  const res = await api.get<ErpProductsPage>(`${BASE}/products`, { ...ROOT_AXIOS, params })
  return res.data
}

export async function getProductDetail(productId: string): Promise<ErpProductDetail> {
  const res = await api.get<ErpProductDetail>(
    `${BASE}/products/${encodeURIComponent(productId)}`,
    ROOT_AXIOS,
  )
  return res.data
}

export async function listStylesForProduct(productId: string): Promise<ErpStyle[]> {
  const res = await api.get<{ items: ErpStyle[] }>(
    `${BASE}/products/${encodeURIComponent(productId)}/styles`,
    ROOT_AXIOS,
  )
  return res.data.items ?? []
}

export async function getStylePreferences(styleId: string): Promise<ErpPreferenceSchema> {
  const res = await api.get<ErpPreferenceSchema>(
    `${BASE}/styles/${encodeURIComponent(styleId)}/preferences`,
    ROOT_AXIOS,
  )
  return res.data
}

export async function getStyleMeasurements(styleId: string): Promise<ErpMeasurementSchema> {
  const res = await api.get<ErpMeasurementSchema>(
    `${BASE}/styles/${encodeURIComponent(styleId)}/measurements`,
    ROOT_AXIOS,
  )
  return res.data
}

export async function getStylePreview(styleId: string): Promise<ErpPreviewSpec> {
  const res = await api.get<ErpPreviewSpec>(
    `${BASE}/styles/${encodeURIComponent(styleId)}/preview`,
    ROOT_AXIOS,
  )
  return res.data
}

export async function getStyleComposite(
  styleId: string,
  attrs: Record<string, unknown>,
  endpoint?: string,
): Promise<ErpCompositeResult> {
  const params: Record<string, string> = {}
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === null || v === '') continue
    params[`attr.${k}`] = String(v)
  }
  const path = endpoint || `${BASE}/styles/${encodeURIComponent(styleId)}/composite`
  const res = await api.get<ErpCompositeResult>(path, { ...ROOT_AXIOS, params })
  return res.data
}

export async function generateAiImage(
  styleId: string,
  attrs: Record<string, unknown>,
): Promise<ErpAiImageResult> {
  const res = await api.post<ErpAiImageResult>(
    `${BASE}/styles/${encodeURIComponent(styleId)}/generate-ai`,
    { attrs },
    ROOT_AXIOS,
  )
  return res.data
}

export async function previewAiImagePayload(
  styleId: string,
  attrs: Record<string, unknown>,
): Promise<ErpAiImagePreview> {
  const res = await api.post<ErpAiImagePreview>(
    `${BASE}/styles/${encodeURIComponent(styleId)}/generate-ai-preview`,
    { attrs },
    ROOT_AXIOS,
  )
  return res.data
}

// ── Sales orders ──

export async function createSalesOrder(input: ErpOrderCreateInput): Promise<ErpOrderResult> {
  const res = await api.post<ErpOrderResult>(`${BASE}/sales-orders`, input, ROOT_AXIOS)
  return res.data
}

// ── Generic helpers ─────────────────────────────────────────
// Used by ecommerce blocks whose data source is a configurable URL
// (the built-in default or a custom REST endpoint). The typed wrappers
// above stay the call site for known shapes; these are for arbitrary
// endpoints whose response shape isn't known ahead of time.

/** Generic GET. Returns raw `response.data` — caller decides how to
 *  unwrap (see `extractItems`). */
export async function apiGet<T = unknown>(
  url: string,
  params?: Record<string, unknown>,
): Promise<T> {
  const cleaned: Record<string, string> = {}
  for (const [k, v] of Object.entries(params || {})) {
    if (v === undefined || v === null || v === '') continue
    cleaned[k] = typeof v === 'string' ? v : String(v)
  }
  const res = await api.get<T>(url, { ...ROOT_AXIOS, params: cleaned })
  return res.data
}

/** Generic POST. Returns raw `response.data`. */
export async function apiPost<T = unknown>(url: string, body: unknown): Promise<T> {
  const res = await api.post<T>(url, body, ROOT_AXIOS)
  return res.data
}

/** Tolerantly extract a `Record[]` from a generic JSON response.
 *  Tries the response itself (if it's already an array), then common
 *  envelope keys: items, results, data, records. Returns an empty
 *  array when no candidate is found. */
export function extractItems(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) return data as Record<string, unknown>[]
  if (!data || typeof data !== 'object') return []
  const d = data as Record<string, unknown>
  for (const key of ['items', 'results', 'data', 'records'] as const) {
    const v = d[key]
    if (Array.isArray(v)) return v as Record<string, unknown>[]
  }
  return []
}

// ── Error message helper — mirrors err?.error?.error?.message in Angular ──
export function erpErrorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: { message?: string; fields?: Record<string, string> } } } }
  return e?.response?.data?.error?.message || fallback
}

export function erpFieldErrors(err: unknown): Record<string, string> | undefined {
  const e = err as { response?: { data?: { error?: { fields?: Record<string, string> } } } }
  const f = e?.response?.data?.error?.fields
  return f && typeof f === 'object' ? f : undefined
}
